import { skipToken } from "@reduxjs/toolkit/query";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useContext, type FC } from "react";
import { useGenerateAudioUrlsQuery } from "../../api/entities/recording.js";
import { AudioPlayerContext } from "../../components/audioPlayer/context.js";
import { RecordingVideoPanel } from "./recordingVideoPanel.js";

interface TranscriptVideoPanelProps {
  recordings: readonly AccountGradingRecording[];
}

/**
 * Shows the active recording's source video (for video-sourced recordings)
 * above the transcript. It is rendered by the transcript pane *between the
 * call-outline header and the scrollable transcript container* — not inside the
 * scroll area — so the video keeps its own space and the transcript scrolls
 * beneath it, rather than transcript text sliding under a sticky overlay.
 *
 * Reads the shared audio player's `nativeAudio` / `currentSourceKey` directly
 * from context (it does not register any sources) so it only mirrors the player:
 * {@link RecordingVideoPanel} slaves the muted video to that audio element.
 */
export const TranscriptVideoPanel: FC<TranscriptVideoPanelProps> = (props) => {
  const { recordings } = props;
  const { currentSourceKey, nativeAudio } = useContext(AudioPlayerContext);

  const { data: audioUrls } = useGenerateAudioUrlsQuery(
    recordings.length > 0
      ? recordings.map((recording) => recording.id)
      : skipToken,
  );

  const activeRecording = recordings.find(
    (recording) => recording.id === currentSourceKey,
  );
  const activeVideoUrl = activeRecording
    ? audioUrls?.find((url) => url.id === activeRecording.id)?.video_url
    : undefined;

  if (!activeVideoUrl) {
    return null;
  }

  return (
    <RecordingVideoPanel
      // Remount when the active recording changes so the video reloads its
      // source and re-syncs to that recording's audio element.
      key={activeRecording?.id}
      videoUrl={activeVideoUrl}
      nativeAudio={nativeAudio}
    />
  );
};
